/**
 * Authentication Utilities
 * Simple password-based auth for admin dashboard
 */

const ADMIN_PASSWORD_HASH = '16e0cf07f63d85d17f4d50160da846b5aefc12bb5fe206cfca380b0fbc585082'; // SHA-256 of "Nn291997!"
const SESSION_KEY = 'adminSession';
const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

interface SessionData {
  token: string;
  timestamp: number;
  expiresAt: number;
}

/**
 * Hash password using SHA-256
 */
async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Generate random session token
 */
function generateToken(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

/**
 * Verify password and create session
 */
export async function login(password: string): Promise<boolean> {
  const hashedInput = await hashPassword(password);
  
  if (hashedInput === ADMIN_PASSWORD_HASH) {
    const timestamp = Date.now();
    const sessionData: SessionData = {
      token: generateToken(),
      timestamp,
      expiresAt: timestamp + SESSION_DURATION,
    };
    
    localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
    return true;
  }
  
  return false;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  try {
    const sessionStr = localStorage.getItem(SESSION_KEY);
    if (!sessionStr) {
      return false;
    }
    
    const session: SessionData = JSON.parse(sessionStr);
    const now = Date.now();
    
    // Check if session is expired
    if (now > session.expiresAt) {
      logout();
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error checking authentication:', error);
    return false;
  }
}

/**
 * Logout and clear session
 */
export function logout(): void {
  localStorage.removeItem(SESSION_KEY);
}

/**
 * Get session data
 */
export function getSession(): SessionData | null {
  try {
    const sessionStr = localStorage.getItem(SESSION_KEY);
    if (!sessionStr) {
      return null;
    }
    
    return JSON.parse(sessionStr);
  } catch (error) {
    console.error('Error getting session:', error);
    return null;
  }
}

/**
 * Rate limiting for login attempts
 */
const LOGIN_ATTEMPTS_KEY = 'loginAttempts';
const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes

interface LoginAttempts {
  count: number;
  firstAttempt: number;
  lockedUntil?: number;
}

/**
 * Check if login is rate limited
 */
export function isLoginRateLimited(): { limited: boolean; remainingTime?: number } {
  try {
    const attemptsStr = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
    if (!attemptsStr) {
      return { limited: false };
    }
    
    const attempts: LoginAttempts = JSON.parse(attemptsStr);
    const now = Date.now();
    
    // Check if locked
    if (attempts.lockedUntil && now < attempts.lockedUntil) {
      const remainingTime = Math.ceil((attempts.lockedUntil - now) / 1000 / 60); // minutes
      return { limited: true, remainingTime };
    }
    
    // Reset if lockout period has passed
    if (attempts.lockedUntil && now >= attempts.lockedUntil) {
      localStorage.removeItem(LOGIN_ATTEMPTS_KEY);
      return { limited: false };
    }
    
    return { limited: false };
  } catch (error) {
    console.error('Error checking rate limit:', error);
    return { limited: false };
  }
}

/**
 * Record failed login attempt
 */
export function recordFailedLogin(): void {
  try {
    const attemptsStr = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
    const now = Date.now();
    
    if (!attemptsStr) {
      const attempts: LoginAttempts = {
        count: 1,
        firstAttempt: now,
      };
      localStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify(attempts));
      return;
    }
    
    const attempts: LoginAttempts = JSON.parse(attemptsStr);
    attempts.count++;
    
    // Lock account if max attempts reached
    if (attempts.count >= MAX_ATTEMPTS) {
      attempts.lockedUntil = now + LOCKOUT_DURATION;
    }
    
    localStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify(attempts));
  } catch (error) {
    console.error('Error recording failed login:', error);
  }
}

/**
 * Clear login attempts on successful login
 */
export function clearLoginAttempts(): void {
  localStorage.removeItem(LOGIN_ATTEMPTS_KEY);
}

